import React, { useId } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useIntl } from 'react-intl';

import { useGetCapacityStatistic } from 'src/features/statistic/api/useGetCapacityStatistic';
import BarChart from 'src/features/statistic/components/chart/BarChart';
import HalfPieChart from 'src/features/statistic/components/chart/HalfPieChart';
import {
    CapacityAverageDuration,
    DataKey,
    TOTAL_VISITS_KEY,
    TransformedBarItem,
    VisitStatisticHourlyBreakdown,
} from 'src/features/statistic/types';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import { SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import StatisticTable from 'src/registry/components/statistic/StatisticTable';
import { Button } from 'src/registry/ui/Button';
import { Input } from 'src/registry/ui/Input';
import SingleDatePicker from 'src/registry/ui/SingleDatePicker';
import { ICON_MAP } from 'src/utils/constants';
import { safeFormatMessage } from 'src/utils/intl';
import { useParking } from 'src/providers/ParkingProvider.tsx';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItemWithIcon,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { tableMessages } from 'src/registry/components/table';
import { useListZones } from 'src/features/zone/api/useListZones';
import { AdministrativeTranslation } from 'src/features/administration/utils/types';
import MapLegend from 'src/features/statistic/components/MapLegend';
import {
    allColorCategoryMapping,
    capacityHourlyBreakdownColorMapping,
    colorCategoryMapping,
} from 'src/features/statistic/utils/constants';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { useMaskInput } from 'use-mask-input';
import ViewAllButton from 'src/components/ViewAllButton';
import { PATH } from 'src/features/navigation/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

dayjs.extend(duration);

type FormattedData = {
    totalVisitsData: {
        title: string;
        value: number;
    }[];
    visitsUntilTimeData: {
        title: string;
        value: number;
    }[];
    capacityData: {
        title: string;
        value: number;
    }[];
};

type SelectedZone = {
    name: string;
    id: string;
};

export default function WidgetCapacity({ date }: { date: dayjs.Dayjs }) {
    const { formatMessage, locale } = useIntl();
    const [zone, setZone] = React.useState<SelectedZone | null>(null);
    const id = useId();
    const accessOccupancyStatistics = useHasPermissions([PERMISSION.ACCESS_OCCUPANCY_STATISTICS]);

    const datetimeMask = useMaskInput({
        mask: 'datetime',
        options: {
            placeholder: '0',
            inputFormat: 'HH:mm',
            outputFormat: 'HH:mm',
            showMaskOnHover: false,
        },
    });

    const columns: ColumnDef<CapacityAverageDuration>[] = [
        {
            accessorKey: 'visitor_type',
            header: formatMessage(statisticMessages.method),
            cell: ({ row }) => {
                return (
                    <div className="flex flex-row items-center gap-1">
                        <div
                            style={{
                                backgroundColor: allColorCategoryMapping[row.original.visitor_type],
                                width: 10,
                                height: 10,
                                borderRadius: 50,
                            }}
                        />
                        <div>{safeFormatMessage(formatMessage, row.original.visitor_type)}</div>
                    </div>
                );
            },
        },
        {
            accessorKey: 'total_visitors_count',
            header: formatMessage(statisticMessages.visitors),
        },
        {
            accessorKey: 'avg_duration_seconds',
            header: formatMessage(statisticMessages.averageDuration),
            cell: ({ row }) => dayjs.duration(row.original.avg_duration_seconds, 'seconds').format('HH:mm:ss'),
        },
    ];

    const translate = React.useCallback(
        (translations?: AdministrativeTranslation[]) => {
            if (!translations) return '-';
            const localeTranslation = translations.find((translation) => translation.language === locale)?.name;
            return localeTranslation ?? '-';
        },
        [locale],
    );

    const [dateTime, setDateTime] = React.useState({
        date,
        time: date.format('HH:mm'),
    });

    const { parkingId } = useParking();

    const { data, isLoading } = useGetCapacityStatistic({
        from_datetime: dateTime.date.format('YYYY-MM-DD'),
        time: dateTime.time,
        zone: zone?.id ?? null,
        parkingId,
    });
    const { data: zoneData } = useListZones({ parkingId });

    const { totalVisitsData, capacityData, visitsUntilTimeData }: FormattedData = React.useMemo(() => {
        if (!data)
            return {
                totalVisitsData: [],
                visitsUntilTimeData: [],
                capacityData: [],
            };
        const totalVisitsData = Object.entries(data.pie_charts_section_stats.total_visits_pie_chart)
            .filter(([key, value]) => value > 0 && key !== TOTAL_VISITS_KEY)
            .map(([key, value]) => ({
                title: key,
                value,
            }));

        const visitsUntilTimeData = Object.entries(data.pie_charts_section_stats.visits_until_time_pie_chart)
            .filter(([key, value]) => value > 0 && key !== TOTAL_VISITS_KEY)
            .map(([key, value]) => ({
                title: key,
                value,
            }));

        const capacityData = Object.entries(data.pie_charts_section_stats.capacity_at_time_pie_chart)
            .filter(([_, value]) => value > 0)
            .map(([key, value]) => ({
                title: key,
                value,
            }));

        return {
            capacityData,
            totalVisitsData,
            visitsUntilTimeData,
        };
    }, [data]);

    const transformPaymentData = React.useCallback(
        ({
            data,
        }: {
            data?: VisitStatisticHourlyBreakdown;
        }): {
            dataKeys: DataKey[];
            data: TransformedBarItem[];
        } => {
            if (!data)
                return {
                    dataKeys: [],
                    data: [],
                };

            const dataKeys: DataKey[] = Array.from(
                Object.values(data).reduce((acc, currentValue) => {
                    Object.keys(currentValue).map((key) => acc.add(key));

                    return acc;
                }, new Set<string>()),
            ).map((key) => ({
                title: safeFormatMessage(formatMessage, `${key}s`, key),
                value: key,
            }));

            const newData: TransformedBarItem[] = Object.entries(data).map(([key, item]) => ({
                ...item,
                title: safeFormatMessage(formatMessage, key.replaceAll('_', '-')),
            }));

            return {
                dataKeys,
                data: newData,
            };
        },
        [formatMessage],
    );

    const params = new URLSearchParams();
    params.set('active_as_of_after', dateTime.date.hour(0).minute(0).format('YYYY-MM-DDTHH:mm'));
    params.set('active_as_of_before', dateTime.date.format('YYYY-MM-DDTHH:mm'));

    if (zone?.id) {
        params.set('zone', zone?.id);
    }

    return (
        <GeneralBlock className="flex-1 gap-6">
            <div className="flex min-h-60 flex-1 flex-col">
                <div className="z-20 flex flex-wrap justify-between">
                    <div className="flex flex-row gap-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="text-sm">
                                    {zone?.name ?? formatMessage(statisticMessages.allZones)}
                                    <FontAwesomeIcon icon={ICON_MAP.DOWN} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                <DropdownMenuLabel>{formatMessage(statisticMessages.chooseZone)}</DropdownMenuLabel>
                                {!zone && <DropdownMenuSeparator />}
                                {zoneData?.results.map((z) => (
                                    <DropdownMenuCheckboxItem
                                        key={z.id}
                                        checked={z.id === zone?.id}
                                        onSelect={() => {
                                            setZone({
                                                id: z.id,
                                                name: translate(z.translations),
                                            });
                                        }}
                                    >
                                        {translate(z.translations)}
                                    </DropdownMenuCheckboxItem>
                                ))}
                                <DropdownMenuSeparator />
                                <DropdownMenuItemWithIcon
                                    variant="destructive"
                                    icon={ICON_MAP.DELETE}
                                    onSelect={() => setZone(null)}
                                >
                                    {formatMessage(tableMessages.clearFilter)}
                                </DropdownMenuItemWithIcon>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <SingleDatePicker
                            value={new Date(dateTime.date.toISOString())}
                            buttonValue={dateTime.date.format('DD.MM.YYYY')}
                            onChange={(date) => setDateTime({ ...dateTime, date: dayjs(date) })}
                        />
                        <div className="relative flex w-32 min-w-32 items-center px-4">
                            <FontAwesomeIcon
                                icon={faClock}
                                size="sm"
                                className={
                                    'text-muted-foreground pointer-events-none absolute top-1/2 left-6 -translate-y-1/2'
                                }
                            />
                            <Input
                                id={id}
                                type="text"
                                placeholder="00-23:59"
                                value={dateTime.time}
                                onChange={({ target }) => setDateTime({ ...dateTime, time: target.value })}
                                ref={datetimeMask}
                                className="pl-10 text-sm"
                            />
                        </div>
                    </div>

                    {accessOccupancyStatistics && params && (
                        <ViewAllButton route={`/${PATH.STATISTICS}/capacity-stats?${params?.toString()}`} newTab />
                    )}
                </div>
                <div className="flex min-h-48 w-full gap-2">
                    <HalfPieChart
                        data={totalVisitsData}
                        dataKey="value"
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.totalVisits)}
                        value={data?.pie_charts_section_stats.total_visits_pie_chart.total_visits_count}
                    />
                    <HalfPieChart
                        data={visitsUntilTimeData}
                        dataKey="value"
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.visitsUntil, { time: dateTime.time })}
                        value={data?.pie_charts_section_stats.visits_until_time_pie_chart.total_visits_count}
                    />
                    <HalfPieChart
                        data={capacityData}
                        dataKey="value"
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.capacityAt, { time: dateTime.time })}
                        value={data?.pie_charts_section_stats.capacity_at_time_pie_chart.total_spaces_count}
                    />
                </div>

                <MapLegend colorMapping={colorCategoryMapping} />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2">
                <StatisticTable
                    className="h-fit flex-1"
                    title={formatMessage(statisticMessages.totalVisitsBreakdownDay)}
                    data={data?.visits_breakdown_for_day_stats ?? []}
                    columns={columns as any}
                />
                <StatisticTable
                    className="h-fit flex-1"
                    title={formatMessage(statisticMessages.totalVisitsBreakdownTime, { time: dateTime.time })}
                    data={data?.visits_breakdown_for_time_stats ?? []}
                    columns={columns as any}
                />
            </div>
            <div className="flex flex-col gap-2">
                <SectionBlockTitle>{formatMessage(statisticMessages.capacityHourlyBreakdownDay)}</SectionBlockTitle>
                <BarChart
                    isLoading={isLoading}
                    colorMapping={capacityHourlyBreakdownColorMapping}
                    {...transformPaymentData({ data: data?.capacity_hourly_breakdown_for_day_stats })}
                />
            </div>
        </GeneralBlock>
    );
}
